import { DataSource } from '@angular/cdk/table';
import { LoadSourceDataModel } from './load-source-data.model';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { MatPaginator, MatSort, MatSelect } from '@angular/material';
import {debounceTime, distinctUntilChanged, finalize, tap} from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { CollectionViewer } from '@angular/cdk/collections';
import { ResponseModel } from '@core/api';
import {ElementRef} from '@angular/core';
import {fromEvent} from 'rxjs/internal/observable/fromEvent';

export interface GridDataFilterData {
  element: ElementRef | MatSelect,
  queryName: string,
  eventName?: string,
  formatValue?: (arg: string) => string
}

export abstract class GridDataSource<T> extends DataSource<T> implements LoadSourceDataModel<T> {

  protected gridDataSubject = new BehaviorSubject<T[]>([]);
  protected loadingSubject = new BehaviorSubject<boolean>(false);
  protected paginatorSubject = new BehaviorSubject<MatPaginator>(null);
  protected sortSubject = new BehaviorSubject<MatSort>(null);
  protected totalSubject = new BehaviorSubject<number>(0);

  public loading$ = this.loadingSubject.asObservable();
  public total$ = this.totalSubject.asObservable();

  protected paginator$ = this.paginatorSubject.asObservable();
  protected sort$ = this.sortSubject.asObservable();

  protected filters: Set<GridDataFilterData> = new Set();

  protected constructor() {
    super();

    this.paginator$
      .pipe(tap(() => this.paginator && this.initPagination()))
      .subscribe();

    this.sort$
      .pipe(tap(() => this.sort && this.initSort()))
      .subscribe();
  }

  addFilter(filter: GridDataFilterData) {
    this.filters.add(filter);

    this.initWatchFilter(filter);
  }

  updateDataSource() {
    this.getFilteredDataSource();
  }

  protected getSubject(filter: GridDataFilterData): Observable<void>  {
    if (filter.element instanceof ElementRef) {
      return fromEvent(filter.element.nativeElement, filter.eventName);
    } else {
      return filter.element.valueChange;
    }
  }

  protected initWatchFilter(filter: GridDataFilterData): void {
    this.getSubject(filter)
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        tap(() => {
          this.getFilteredDataSource();
        })
      )
      .subscribe();
  }

  protected getValue(filter: GridDataFilterData): string {
    let value :string;
    if (filter.element instanceof ElementRef) {
      value = filter.element.nativeElement.value;    
    } else {
      value = filter.element.value     
    }
    
    if(filter.formatValue){
      value = filter.formatValue(value);
    }
    return value;
  }

  protected getFilteredDataSource(httpParam = new HttpParams()): void {

    this.filters.forEach(filter => {
      const value = this.getValue(filter);

      if (value) {
        httpParam = httpParam.set(filter.queryName, value);
      }
    });

    this.firstPage();
    this.loadData(
      httpParam
    );
  };

  get paginator(): MatPaginator {
    return this.paginatorSubject.value;
  }

  set paginator(pagination: MatPaginator) {
    this.paginatorSubject.next(pagination);
  }

  get sort(): MatSort {
    return this.sortSubject.value;
  }

  set sort(sort: MatSort) {
    this.sortSubject.next(sort);
  }

  get data(): T[] {
    return this.gridDataSubject.value;
  }

  get defaultHttpParams(): HttpParams {
    let defaultHttpParams = new HttpParams();

    if (this.filters) {
      this.filters.forEach(filter => {
        const value = this.getValue(filter);
        if (value) {
          defaultHttpParams = defaultHttpParams.set(filter.queryName, value);
        }
      });
    }

    if (this.paginator) {
      defaultHttpParams = defaultHttpParams.set('page', (this.paginator.pageIndex + 1).toString());
      defaultHttpParams = defaultHttpParams.set('per_page', this.paginator.pageSize.toString());
    }

    if (this.sort && this.sort.direction) {
      defaultHttpParams = defaultHttpParams.set('sort', `${ this.getDef(this.sort.direction) }${ this.sort.active }`);
    }

    return defaultHttpParams;
  }

  firstPage() {
    this.paginator && (this.paginator.pageIndex = 0);
  }

  connect(collectionViewer: CollectionViewer): Observable<T[]> {
    return this.gridDataSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.gridDataSubject.complete();
    this.loadingSubject.complete();
    this.paginatorSubject.complete();
  }

  protected abstract loader(defaultHttpParams: HttpParams): Observable<ResponseModel<T[]>>;

  public loadData(customHttpParams: HttpParams = null): void {
    if (this.loadingSubject.value) {
      return;
    }

    this.loadingSubject.next(true);

    let httpParams = this.defaultHttpParams;

    customHttpParams && customHttpParams.keys().forEach(key => {
      httpParams = httpParams.set(key, customHttpParams.get(key));
    });

    this.loader(httpParams)
      .pipe(finalize(() => this.loadingSubject.next(false)))
      .subscribe(res => {
        this.gridDataSubject.next(res.data);
        this.totalSubject.next(res.meta.total);
      });
  }

  getDef(direction) {
    switch (direction) {
      case 'desc':
        return '-';
      case 'asc':
      default:
        return '';
    }
  }

  private initPagination() {
    this.paginator.page
      .pipe(tap(() => this.loadData()))
      .subscribe();
  }

  private initSort() {
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

    merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => this.loadData())
      )
      .subscribe();
  }
}
